import React, { useContext } from 'react';
import grayHeartIconMobileNew from '@assets/images/gray-heart-iconMobileNew.svg';
import redHeartIconMobileNew from '@assets/images/red-heart-iconMobileNew.svg';
import badgeFire from '@assets/images/badge-fire.svg';
import Image from 'next/image';
import HotelApi from '@customTypes/hotel-api';
import { getMonthAndYear } from '@helpers/date';
import { store } from '@context/store';
import showClosedForSeasonBanner from '@helpers/showClosedForSeasonBanner';
import dateWithinLastNDays from '@helpers/dateWithinLastNDays';
import stopwatchIcon from '@assets/images/stopwatch-icon.svg';
import useHotSpot from '@hooks/useHotSpot';
import { parseISO } from 'date-fns';
import { HotSpotMin } from '@customTypes/hot-spot';
import SwiperCarousel from '../SwiperCarousel/SwiperCarousel';

type Slide = {
  key: string;
  url: string;
  imgClassnames: string;
};

type Props = {
  classes?: string;
  name: string;
  navigateToDetailsPage: () => void;
  images?: Slide[];
  userFavorites: any;
  handleAddingHotelIdToFavorites: any;
  hotel: HotelApi;
};

export default function Gallery({
  classes = '',
  name,
  images = [],
  userFavorites,
  handleAddingHotelIdToFavorites,
  navigateToDetailsPage,
  hotel,
}: Props) {
  const globalState = useContext(store);
  const { state } = globalState;
  const { searchDate } = state;

  const renderBadge = (createdAt: string, closedForSeason: boolean) =>
    !closedForSeason && dateWithinLastNDays(createdAt, 90) && !hotel.hotSpot;

  const { getBadgeText } = useHotSpot();

  return (
    <>
      <div className="block desktop:hidden absolute z-40 right-4 mt-4">
        <Image
          src={
            userFavorites && userFavorites.includes(hotel.id.toString())
              ? redHeartIconMobileNew
              : grayHeartIconMobileNew
          }
          alt="heart"
          onClick={() => handleAddingHotelIdToFavorites(hotel)}
        />
      </div>

      {renderBadge(hotel.createdAt, hotel.closedForSeason) && (
        <div className="absolute flex items-center z-40 right-4 bottom-4 py-1 h-6 whitespace-nowrap text-white bg-rp-secondary-orange-dark rounded-xl px-3 tracking-wide font-rp-basetica text-xs">
          <div className="mr-1.5 mt-0.5">
            <Image src={badgeFire} alt="badge-fire" />
          </div>
          <div>Recently Added</div>
        </div>
      )}
      {hotel.closedForSeason && showClosedForSeasonBanner(hotel.reopenDate, searchDate) && (
        <div className="w-full relative flex justify-center">
          <div className="flex flex-col gap-0 items-center z-40 absolute pt-1 pb-0.5 whitespace-nowrap text-rp-primary-black bg-rp-white-faded backdrop-blur-3xl rounded-l-full rounded-r-full text-13 font-rp-pn-semi-bold px-6 tracking-wide mt-3">
            <div className="opacity-100">CLOSED FOR THE SEASON</div>
            {hotel.reopenDate && (
              <div className="text-rp-dark-gray-2 -mt-0.5">
                {`Opens ${getMonthAndYear(hotel.reopenDate)}`}
              </div>
            )}
          </div>
        </div>
      )}
      <div
        className={`${classes} relative w-full flex-shrink-0 min-h-250px desktop:min-h-297px desktop:min-w-445px desktop:max-w-445px`}
      >
        <div className="rounded-xl">
          {hotel.hotSpot ? (
            <>
              <div
                className="absolute bg-rp-hot-spot flex justify-center rounded z-10 -top-6 desktop:-top-8 desktop:hidden"
                style={{ width: '100%', height: '100%' }}
              >
                <span className="text-white font-rp-basetica text-xs mt-1">Hot Spot Savings</span>
              </div>
              <div
                className="absolute z-20 bg-white rounded-none"
                style={{ width: '100%', height: '100%' }}
              />
              <div className="absolute bg-white z-40 bottom-4 right-4 rounded-xl bg-rp-hot-spot px-2.5 py-1 h-6 flex items-center gap-5px">
                <Image src={stopwatchIcon} alt="Stopwatch Icon" />
                <span className="font-rp-basetica text-xs text-white">
                  {getBadgeText(
                    parseISO(hotel.hotSpot.booking_applicable_end_date),
                    hotel.hotSpot as HotSpotMin,
                  )}
                </span>
              </div>
            </>
          ) : null}

          <SwiperCarousel
            slides={images}
            slidesPerView={1}
            carouselClass={`dlp-carousel ${hotel.hotSpot ? 'z-30 hot-spot' : ''}`}
            spaceBetween={8}
            alt={name}
            isSRP
            handleClick={navigateToDetailsPage}
            isNavigationEnabled={false}
            startingSlideIndex={1}
            newHotelCardDesign
            lazy
          />
        </div>
      </div>
    </>
  );
}
